import { log } from 'log';
import { pick } from 'ramda';
import { useCallback } from 'react';
import { useGetPlayerTags } from 'server';
import { workerService } from 'js/services/worker.service';
import { useDeviceSelector, useUserSelector } from 'js/store/hooks';

import * as Types from './types';
import * as helpers from './helpers';
import { useFetchBannerFeed } from '../useFetchBannerFeed';
import { IBannerFeed, IBannerSlide, ISliderOptions } from '../../types';

export const useGetSlides = () => {
    const fetchBannerFeed = useFetchBannerFeed();
    const { formFactor, os, browserName } = useDeviceSelector(pick(['formFactor', 'os', 'browserName']));
    const { isLoggedIn, lang, urlTemplates, country } = useUserSelector(pick(['isLoggedIn', 'lang', 'urlTemplates', 'country']));
    const getPlayerTags = useGetPlayerTags();

    return useCallback(async () => {
        try {
            const bannerFeed = await fetchBannerFeed();

            // request slider options from the worker
            const sliderOptions = await workerService.request<{
                bannerFeed: IBannerFeed;
                formFactor: string;
            }, ISliderOptions>('sliderOptions', {
                bannerFeed,
                formFactor
            });

            // fetch player tags directly, as it involves an API call
            const playerTags = await helpers.getSliderPlayerTags({ bannerFeed, isLoggedIn, getPlayerTags });

            // request slides from the worker, passing the player tags
            const slides = await workerService.request<{
                bannerFeed: IBannerFeed;
                options: Types.IGetSlidesOptions;
            }, IBannerSlide[]>('slides', {
                bannerFeed,
                options: {
                    os,
                    isLoggedIn,
                    formFactor,
                    playerTags,
                    browserName,
                    cookies: document.cookie,
                    userLanguage: lang ?? '',
                    userCountry: country ?? '',
                    segmentationURLTemplates: urlTemplates ?? [],
                    urlSearchParams: Object.fromEntries(new URLSearchParams(location.search))
                }
            });

            return { sliderOptions, slides };
        } catch (error) {
            log('error', error);

            return { sliderOptions: {} as ISliderOptions, slides: [] };
        }
    }, [os, lang, country, isLoggedIn, formFactor, browserName, getPlayerTags, urlTemplates, fetchBannerFeed]);
};
